import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Progress,
  Table,
  Space,
  Tag,
  Avatar,
  Spin,
} from "antd";
import { useList } from "@refinedev/core";
import { apiService } from "@/services/api";
import { DashboardStats } from "@/types";
import {
  UserOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  EditOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

export const DashboardPage: React.FC = () => {
  const { data: contentsData, isLoading: contentsLoading } = useList({
    resource: "contents",
    pagination: { current: 1, pageSize: 5 },
  });

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const dashboardStats = await apiService.getDashboardStats();
        setStats(dashboardStats);
      } catch (e) {
        console.error("Failed to fetch dashboard stats:", e);
      } finally {
        setStatsLoading(false);
      }
    })();
  }, []);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const totalContents = stats?.totalContents || 0;
  const publishedContents = stats?.publishedContents || 0;
  const draftContents = stats?.draftContents || 0;
  const pendingContents = stats?.pendingContents || 0;

  const publishedPercent = totalContents > 0 ? Math.round((publishedContents / totalContents) * 100) : 0;
  const draftPercent = totalContents > 0 ? Math.round((draftContents / totalContents) * 100) : 0;
  const pendingPercent = totalContents > 0 ? Math.round((pendingContents / totalContents) * 100) : 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Dashboard
        </Title>
        <Text type="secondary">Vue d'ensemble de votre plateforme Filmika</Text>
      </div>

      {/* Statistics Cards */}
      <Spin spinning={statsLoading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Utilisateurs"
                value={stats?.totalUsers || 0}
                formatter={(value) => formatNumber(Number(value))}
                prefix={<UserOutlined style={{ color: "#1890ff" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Contenus"
                value={totalContents}
                formatter={(value) => formatNumber(Number(value))}
                prefix={<PlayCircleOutlined style={{ color: "#52c41a" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Contenus Publiés"
                value={publishedContents}
                formatter={(value) => formatNumber(Number(value))}
                prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Brouillons / En attente"
                value={draftContents + pendingContents}
                formatter={(value) => formatNumber(Number(value))}
                prefix={<EditOutlined style={{ color: "#faad14" }} />}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Content Status + Recent Content */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={8}>
          <Card title="Contenus par statut" style={{ height: 400 }}>
            <Spin spinning={statsLoading}>
              <div style={{ padding: "20px 0" }}>
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <Text><CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />Publiés</Text>
                    <Text strong>{publishedContents}</Text>
                  </div>
                  <Progress percent={publishedPercent} strokeColor="#52c41a" />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <Text><EditOutlined style={{ color: "#1890ff", marginRight: 8 }} />Brouillons</Text>
                    <Text strong>{draftContents}</Text>
                  </div>
                  <Progress percent={draftPercent} strokeColor="#1890ff" />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <Text><ClockCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />En attente</Text>
                    <Text strong>{pendingContents}</Text>
                  </div>
                  <Progress percent={pendingPercent} strokeColor="#faad14" />
                </div>
                <div style={{ marginTop: 24, textAlign: "center" }}>
                  <Text type="secondary">Total : {totalContents} contenus</Text>
                </div>
              </div>
            </Spin>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card
            title="Contenus récents"
            extra={<a href="/content">Voir tout</a>}
            style={{ height: 400 }}
          >
            <Table
              dataSource={contentsData?.data}
              loading={contentsLoading}
              pagination={false}
              size="small"
              rowKey="id"
              columns={[
                {
                  title: "Contenu",
                  dataIndex: "title",
                  key: "title",
                  render: (title: string, record: any) => (
                    <Space>
                      <Avatar shape="square" size={40} src={record.posterUrl || record.poster_path}>
                        <PlayCircleOutlined />
                      </Avatar>
                      <div>
                        <div style={{ fontWeight: 500 }}>{title}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.type?.title}
                        </Text>
                      </div>
                    </Space>
                  ),
                },
                {
                  title: "Statut",
                  dataIndex: "status",
                  key: "status",
                  width: 120,
                  render: (status: string) => {
                    const colors: Record<string, string> = {
                      PUBLISHED: "green",
                      DRAFT: "blue",
                      PENDING: "orange",
                      ARCHIVED: "red",
                    };
                    return <Tag color={colors[status] || "default"}>{status}</Tag>;
                  },
                },
                {
                  title: "Genres",
                  dataIndex: "genres",
                  key: "genres",
                  width: 200,
                  render: (genres: any[]) =>
                    genres?.slice(0, 2).map((g: any) => (
                      <Tag key={g.id}>{g.title}</Tag>
                    )),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Résumé rapide">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Total Contenus"
                  value={totalContents}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Publiés"
                  value={publishedContents}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Brouillons"
                  value={draftContents}
                  valueStyle={{ color: "#1890ff" }}
                  prefix={<EditOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="En attente"
                  value={pendingContents}
                  valueStyle={{ color: "#faad14" }}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
